import type { ReactNode } from "react";
import { createContext, useContext, useEffect, useLayoutEffect, useMemo, useState } from "react";

import { useLocation, useNavigationType } from "@shared/react-router-dom/react-router-dom";

interface NavigationHistoryEntry {
  hash: string;
  key: string;
  pathname: string;
  search: string;
}

interface NavigationHistory {
  backwardEntry: NavigationHistoryEntry | undefined;
  canGoBackward: boolean;
  canGoForward: boolean;
  entries: readonly NavigationHistoryEntry[];
  forwardEntry: NavigationHistoryEntry | undefined;
  index: number;
}

interface Props {
  children: ReactNode;
}

const NavigationHistoryContext = createContext<NavigationHistory | null>(null);
const useIsomorphicLayoutEffect = typeof window === "undefined" ? useEffect : useLayoutEffect;

/** Returns whether two entries identify the same router history location. */
const areHistoryEntriesEqual = (first: NavigationHistoryEntry, second: NavigationHistoryEntry) => {
  return (
    first.key === second.key &&
    first.pathname === second.pathname &&
    first.search === second.search &&
    first.hash === second.hash
  );
};

/** Builds history state and derives the adjacent entries from the active index. */
const createHistory = (
  entries: readonly NavigationHistoryEntry[],
  index: number
): NavigationHistory => ({
  backwardEntry: entries[index - 1],
  canGoBackward: index > 0,
  canGoForward: index < entries.length - 1,
  entries,
  forwardEntry: entries[index + 1],
  index,
});

const pushEntry = (
  history: NavigationHistory,
  entry: NavigationHistoryEntry
): NavigationHistory => {
  const entries = [...history.entries.slice(0, history.index + 1), entry];

  return createHistory(entries, entries.length - 1);
};

const replaceEntry = (
  history: NavigationHistory,
  entry: NavigationHistoryEntry
): NavigationHistory => {
  const entries = history.entries.map((historyEntry, index) =>
    index === history.index ? entry : historyEntry
  );

  return createHistory(entries, history.index);
};

const followEntry = (
  history: NavigationHistory,
  entry: NavigationHistoryEntry
): NavigationHistory => {
  const index = history.entries.findIndex(historyEntry => historyEntry.key === entry.key);

  return index >= 0 ? createHistory(history.entries, index) : createHistory([entry], 0);
};

const updateHistory = (
  history: NavigationHistory,
  entry: NavigationHistoryEntry,
  navigationType: ReturnType<typeof useNavigationType>
): NavigationHistory => {
  const currentEntry = history.entries[history.index];

  if (currentEntry && areHistoryEntriesEqual(currentEntry, entry)) {
    return history;
  }

  if (navigationType === "PUSH") {
    return pushEntry(history, entry);
  }

  if (navigationType === "REPLACE") {
    return replaceEntry(history, entry);
  }

  return followEntry(history, entry);
};

const NavigationHistoryProvider = ({ children }: Props) => {
  const location = useLocation();
  const navigationType = useNavigationType();
  const entry = useMemo<NavigationHistoryEntry>(
    () => ({
      hash: location.hash,
      key: location.key,
      pathname: location.pathname,
      search: location.search,
    }),
    [location]
  );
  const [history, setHistory] = useState(() => createHistory([entry], 0));

  useIsomorphicLayoutEffect(() => {
    setHistory(currentHistory => updateHistory(currentHistory, entry, navigationType));
  }, [entry, navigationType]);

  return <NavigationHistoryContext value={history}>{children}</NavigationHistoryContext>;
};

const useNavigationHistory = () => {
  const navigationHistory = useContext(NavigationHistoryContext);

  if (!navigationHistory) {
    throw new Error("useNavigationHistory must be used within NavigationHistoryProvider");
  }

  return navigationHistory;
};

export {
  NavigationHistoryProvider,
  useNavigationHistory,
  type NavigationHistory,
  type NavigationHistoryEntry,
};
